import CustomStore from 'devextreme/data/custom_store';
import stringify from '../utils/stableStringify';
import { unpack, pack } from 'msgpackr';
import AntAPI from '../utils/AntAPI';
import AntTime from '../utils/AntTime';
import AntArray from '../utils/AntArray';

const isEmptyCellValue = (value) => {
    if (value === undefined || value === null || value === 0) return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === 'string') {
        const text = value.trim();
        return text === '' || (/^[-+]?0*(\.0*)?$/.test(text) && /0/.test(text));
    }
    return false;
};

export default class AntQueryStore {
    constructor(args) {
        this.config = args;
        this.getSearchConditions = args.getSearchConditions;
        this.getParentSearchConditions = args.getParentSearchConditions;
        this.getFilteredGroupConditions = args.getFilteredGroupConditions;
        this.getOrderings = args.getOrderings;
        this.getQueryFields = args.getQueryFields;
        this.getSearchText = args.getSearchText;
        this.getScanBarcode = args.getScanBarcode;
        this.getPageSize = args.getPageSize;
        this.getPageIndex = args.getPageIndex;
        this.setPageIndex = args.setPageIndex;
        this.getOrderBy = args.getOrderBy;
        this.getOrderType = args.getOrderType;
        this.isAllowQuery = args.isAllowQuery;
        this.isShowEdittedRowsOnly = args.isShowEdittedRowsOnly;
        this.onGotData = args.onGotData;
        this.onLoaded = args.onLoaded;
        this.onLoading = args.onLoading;
        this.onFiltering = args.onFiltering;
        // Lưới nhập trực tiếp: dòng nháp chèn cuối trang (không tính vào totalCount/currentData).
        this.getAppendRows = args.getAppendRows;
        this.onUpdated = args.onUpdated;

        this.isScrolling = args.isScrolling !== undefined ? args.isScrolling : false;
        this.remotePaging = args.remotePaging !== undefined ? args.remotePaging : true;
        this.allowNew = args.allowNew !== undefined ? args.allowNew : false;
        this.updateMode = args.updateMode !== undefined ? args.updateMode : 'local';
        this.loadFunc = args.loadFunc ? args.loadFunc : 'getList';

        this.groupByField = args.groupByField;

        this.API = args.API;
        this.url = args.url;

        this.initConditions = this.initConditions.bind(this);
        this.callAPI = this.callAPI.bind(this);
        this.callDownloadAPI = this.callDownloadAPI.bind(this);
        this.callCountAPI = this.callCountAPI.bind(this);
        this.callCountsAPI = this.callCountsAPI.bind(this);
        this.callScanBarcode = this.callScanBarcode.bind(this);
        this.loadDataSource = this.loadDataSource.bind(this);
        this.updateDataSource = this.updateDataSource.bind(this);
        this.updateItem = this.updateItem.bind(this);
        this.refresh = this.refresh.bind(this);
        this.cleanEdited = this.cleanEdited.bind(this);
        this.cleanCached = this.cleanCached.bind(this);
        this.reset = this.reset.bind(this);
        this.resetCurrentData = this.resetCurrentData.bind(this);
        this.replaceCurrentRows = this.replaceCurrentRows.bind(this);
        this.getCurrentData = this.getCurrentData.bind(this);
        this.getCurrentItem = this.getCurrentItem.bind(this);
        this.getCachedData = this.getCachedData.bind(this);
        this.getCachedItem = this.getCachedItem.bind(this);
        this.getEditedData = this.getEditedData.bind(this);
        this.getEditedItem = this.getEditedItem.bind(this);
        this.getNewData = this.getNewData.bind(this);
        this.getItem = this.getItem.bind(this);
        this.getItemByKey = this.getItemByKey.bind(this);
        this.resetEditedItem = this.resetEditedItem.bind(this);
        this.cleanEditedItem = this.cleanEditedItem.bind(this);
        this.willGetLastResult = this.willGetLastResult.bind(this);
        this.addDefaultData = this.addDefaultData.bind(this);
        this.isSelectedAll = this.isSelectedAll.bind(this);
        this.isQuerried = this.isQuerried.bind(this);
        this.isOnePageOnly = this.isOnePageOnly.bind(this);

        this.options = {
            key: args.key ? args.key : 'id',
            load: this.loadDataSource,
            update: this.updateDataSource,
            byKey: this.getItemByKey
            // cacheRawData:false
        };
        this.dataSource = new CustomStore(this.options);

        this.currentData = {
            data: [],
            totalCount: 0,
            conditions: []
        };

        this.cachedItems = [];
        this.editedItems = [];
        this.newItems = [];
        this.lastResult = null;
        this.isGetLastResult = false;
        this.forceQueryOptions = null;
        // A response is only allowed to update the shared grid store when it
        // belongs to the most recently started list request.
        this.listRequestVersion = 0;
        if (this.allowNew) this.newItems.push({ [this.options.key]: AntTime.nowUnix() });
    }

    instance() {
        return this.dataSource;
    }

    getItem(keyValue) {
        var keyName = this.options.key;
        var item;
        if (this.editedItems instanceof Array) item = this.editedItems.find((c) => c && c[keyName] === keyValue);
        if (!item && this.newItems instanceof Array) item = this.newItems.find((c) => c && c[keyName] === keyValue);
        if (!item && this.currentData.data instanceof Array) item = this.currentData.data.find((c) => c && c[keyName] === keyValue);
        if (!item && this.cachedItems instanceof Array) item = this.cachedItems.find((c) => c && c[keyName] === keyValue);

        return item;
    }

    async getItemByKey(keyValue) {
        if (keyValue === undefined || keyValue === null) return null;

        var cachedItem = this.getItem(keyValue);
        if (cachedItem) return cachedItem;
        if (!this.API || this.allowNew) return null;

        var conditionsObject = {};
        var conditionGetters = [this.getSearchConditions, this.getParentSearchConditions, this.getFilteredGroupConditions];
        conditionGetters.forEach((getConditions) => {
            if (getConditions && getConditions.constructor.name === 'Function') {
                var values = getConditions();
                if (values) {
                    Object.keys(values).forEach((name) => {
                        if (values[name] !== undefined) conditionsObject[name] = values[name];
                    });
                }
            }
        });

        var keyName = this.options.key;
        conditionsObject[keyName] = keyValue;
        var conditions = Object.keys(conditionsObject).map((name) => ({ name: name, value: conditionsObject[name] }));

        if (this.getQueryFields && this.getQueryFields.constructor.name === 'Function') {
            var fields = this.getQueryFields();
            if (fields instanceof Array && fields.length > 0) conditions.push({ name: 'selectFields', value: fields });
        }
        if (this.remotePaging) {
            conditions.push({ name: 'pageSize', value: 1 });
            conditions.push({ name: 'pageIndex', value: 0 });
        }

        try {
            var results;
            if (this.url) {
                results = await this.API.getList(conditions, this.url, { byPassDefaultConds: true, byPassTotal: true });
            } else if (this.loadFunc) {
                results = await AntAPI.prototype[this.loadFunc].call(this.API, conditions, null, { byPassTotal: true });
            } else {
                results = await this.API.getList(conditions, null, { byPassTotal: true });
            }

            var rows = results && results.rows instanceof Array ? results.rows : [];
            var item = rows.find((row) => row && row[keyName] === keyValue) || null;
            if (item) this.addDefaultData([item]);
            return item;
        } catch {
            return null;
        }
    }

    getEditedData() {
        return [...this.editedItems];
    }

    getEditedItem(keyValue) {
        var keyName = this.options.key;
        if (this.editedItems instanceof Array) return this.editedItems.find((c) => c && c[keyName] === keyValue);

        return null;
    }

    getNewData() {
        return [...this.newItems];
    }

    getCurrentData() {
        return this.currentData.data;
    }

    isOnePageOnly() {
        if (!this.pageSize) {
            if (this.getPageSize && this.getPageSize.constructor.name == 'Function') this.pageSize = this.getPageSize();
        }
        if (!this.pageSize) return true;
        if (!this.isQuerried()) return true;
        if (!this.currentData) return true;

        if (this.currentData.totalCount) return this.pageSize >= this.currentData.totalCount;

        if (this.currentData.data instanceof Array === false) return true;

        return this.pageSize >= this.currentData.data.length;
    }

    getCurrentItem(keyValue) {
        var keyName = this.options.key;
        var ar = this.getCurrentData();
        if (ar instanceof Array) return ar.find((c) => c && c[keyName] === keyValue);

        return null;
    }

    getCachedData() {
        return this.cachedItems;
    }

    addDefaultData(items) {
        if (this.cachedItems instanceof Array && items instanceof Array) {
            var keyName = this.options.key;
            for (let i = 0; i < items.length; i++) {
                const item = items[i];

                var found = this.cachedItems.find((f) => f[keyName] === item[keyName]);
                if (!found) {
                    var obj = {
                        ...item,
                        [keyName]: item[keyName]
                    };
                    this.cachedItems.push(obj);
                    return obj;
                }
            }
        }
    }

    getCachedItem(keyValue) {
        var keyName = this.options.key;
        if (this.cachedItems instanceof Array) return this.cachedItems.find((c) => c && c[keyName] === keyValue);

        return null;
    }

    async refresh() {
        await this.dataSource.load();
    }
    async reset(cleanCached, cleanEdited) {
        await this.resetCurrentData(null, cleanCached, cleanEdited);
    }

    isSelectedAll() {
        return this.currentData && this.editedItems && this.currentData.totalCount > 0 && this.currentData.totalCount === this.editedItems.length;
    }

    cleanEdited() {
        this.editedItems = [];
        this.newItems = [];
        if (this.allowNew) this.newItems.push({ [this.options.key]: AntTime.nowUnix() });
    }
    cleanCached() {
        this.cachedItems = [];
    }

    async resetCurrentData(obj, cleanCached, cleanEdited) {
        if (cleanEdited) {
            this.cleanEdited();
        }

        if (cleanCached) {
            this.cleanCached();
        }

        if (!obj || obj.data instanceof Array == false) {
            this.currentData = {
                data: [],
                totalCount: 0,
                conditions: []
            };
        } else {
            this.currentData = obj;

            if (this.currentData && this.currentData.data instanceof Array) {
                for (let i = 0; i < this.currentData.data.length; i++) {
                    const row = this.currentData.data[i];
                    if (!row.id) this.cachedItems.push(row);
                    else {
                        var found = this.cachedItems.find((c) => c.id === row.id);
                        if (!found) this.cachedItems.push(row);
                        else {
                            let keys = Object.keys(row);
                            for (let j = 0; j < keys.length; j++) {
                                const k = keys[j];
                                if (k !== 'id') found[k] = row[k];
                            }
                        }
                    }
                }
            }
        }
        if (obj && obj.data instanceof Array) if (this.onLoaded) this.onLoaded(this.currentData);
    }

    // Nhận snapshot dòng đã đi kèm response của action cha. Lần refresh kế tiếp
    // của DevExtreme đọc lại lastResult này, không phát thêm request /list.
    async replaceCurrentRows(rows) {
        if (!(rows instanceof Array)) return false;
        const normalizedRows = rows.map((row, index) => ({ ...row, rowIndex: index + 1 }));
        const conditions = this.currentData?.conditions instanceof Array ? this.currentData.conditions : [];
        const value = {
            queried: true,
            rows: normalizedRows,
            totalCount: normalizedRows.length
        };
        this.lastResult = {
            conditions: JSON.parse(stringify(conditions)),
            value
        };
        this.isGetLastResult = true;
        await this.resetCurrentData(
            {
                data: normalizedRows,
                totalCount: normalizedRows.length,
                conditions
            },
            true,
            true
        );
        return true;
    }

    // Cột trống trên trang hiện tại (để tự ẩn): bỏ qua dòng chèn thêm (dòng nháp nhập trực tiếp — có sẵn giá trị
    // mặc định); coi là trống: null/undefined, chuỗi rỗng hoặc toàn khoảng trắng, 0 (kể cả số dạng chuỗi "0.00"),
    // mảng rỗng.
    isEmptyColumn(colName) {
        return this.isEmptyColumns([colName]);
    }

    isEmptyColumns(colNames) {
        if (!this.lastResult || !this.isQuerried() || !this.currentData || !(this.currentData.data instanceof Array)) return false;
        const appendRows = typeof this.getAppendRows === 'function' ? this.getAppendRows() : null;
        const appendKeys = new Set((appendRows instanceof Array ? appendRows : []).map((r) => r && r.id));
        const rows = this.currentData.data.filter((r) => r && !appendKeys.has(r.id));
        if (rows.length === 0) return false;
        return (colNames instanceof Array ? colNames : []).every((colName) => rows.every((row) => isEmptyCellValue(row[colName])));
    }

    isSameValueColumn(colName) {
        if (!this.lastResult) return false;

        if (this.currentData && this.currentData.totalCount > 0 && this.currentData.totalCount === this.currentData.data.length) {
            return (
                this.currentData.data.map((d) => d[colName]).filter((f) => f !== undefined && f !== null && f !== '' && f !== 0).length ===
                this.currentData.totalCount
            );
        }
        return false;
    }

    async isQuerried() {
        return Boolean(this.lastResult);
    }

    async willGetLastResult() {
        this.isGetLastResult = true;
    }

    requestCacheRefresh(options = {}) {
        this.forceQueryOptions = options;
        this.isGetLastResult = false;
    }

    // Dòng chèn thêm cuối trang (dòng nháp của lưới nhập trực tiếp). Áp cho MỌI đường trả dữ liệu —
    // kể cả lúc trả lại currentData (lượt nạp bị thay, chưa được phép truy vấn, về trang đầu) — nếu không
    // dòng nháp biến mất đúng lúc DevExtreme vẽ kết quả đó. Không đổi currentData/totalCount.
    withAppendRows(result) {
        if (!result || !(result.data instanceof Array) || typeof this.getAppendRows !== 'function' || this.groupByField) return result;
        const appendRows = this.getAppendRows();
        if (!(appendRows instanceof Array) || appendRows.length === 0) return result;
        const keys = new Set(appendRows.map((r) => r && r.id));
        return { ...result, data: [...result.data.filter((r) => !r || !keys.has(r.id)), ...appendRows] };
    }

    async loadDataSource(opts) {
        // console.log('ant-log-loadDataSource : ',opts);
        // if (this.remotePaging && opts && (opts.take === undefined || opts.skip === undefined)) return this.withAppendRows(this.currentData);

        if (opts && ((opts.take === undefined && opts.skip !== undefined) || (opts.take !== undefined && opts.skip === undefined)))
            return this.withAppendRows(this.currentData);

        const requestVersion = ++this.listRequestVersion;
        var conditions = await this.initConditions(opts);

        // if (this.remotePaging === false && this.isScrolling) {
        //     if (this.lastResult) {
        //         var obj1 = {};
        //         conditions.forEach((c) => {
        //             obj1[c.name] = c.value;
        //         });

        //         var obj2 = {};
        //         this.lastResult.conditions.forEach((c) => {
        //             obj2[c.name] = c.value;
        //         });
        //         var isSame = stringify(obj1) === stringify(obj2);
        //         if (isSame) {
        //             if (this.onGotData) await this.onGotData(this.currentData, this.cachedItems);
        //             return this.withAppendRows(this.currentData);
        //         }
        //     }
        // }

        var isAllowQuery = true;

        if (this.isAllowQuery && this.isAllowQuery.constructor.name == 'Function') isAllowQuery = this.isAllowQuery();

        if (!isAllowQuery) {
            if (this.groupByField) {
                var ar = this.currentData && this.currentData.data instanceof Array ? this.currentData.data : [];

                var groups = AntArray.groupBy(
                    ar.map((d) => {
                        return {
                            ...d,
                            key: String(d[this.groupByField])
                        };
                    }),
                    'key'
                );

                var ar = Object.keys(groups).map((key) => {
                    return {
                        key: key,
                        items: groups[key]
                    };
                });

                if (this.onGotData) {
                    await this.onGotData(
                        {
                            data: ar,
                            totalCount: result.totalCount
                        },
                        this.cachedItems
                    );
                }

                return {
                    data: ar,
                    totalCount: result.totalCount
                };
            }

            if (this.onGotData) {
                await this.onGotData(this.currentData, this.cachedItems);
            }
            return this.withAppendRows(this.currentData);
        }

        this.currentOpts = opts;
        var keyName = this.options.key;
        var isGetEditted = false;
        if (this.isShowEdittedRowsOnly && this.isShowEdittedRowsOnly.constructor.name == 'Function')
            isGetEditted = await this.isShowEdittedRowsOnly();

        var result;

        if (isGetEditted) {
            if (this.lastResult && stringify(this.lastResult.conditions) !== stringify(conditions) && !conditions.notAllowQuery)
                await this.callAPI(opts, conditions, requestVersion);
            if (requestVersion !== this.listRequestVersion) return this.withAppendRows(this.currentData);

            result = {
                data: [...this.editedItems],
                totalCount: parseInt(this.editedItems.length)
            };

            if (this.allowNew && this.newItems instanceof Array && this.newItems.length > 0) {
                for (let i = 0; i < this.newItems.length; i++) {
                    const n = this.newItems[i];
                    result.data.push(n);
                    result.totalCount++;
                }
            }
            if (this.cachedItems.length > 0)
                result.data = result.data.map((e) => {
                    var obj = this.cachedItems.find((c) => c[keyName] === e[keyName]);
                    return { ...obj, ...e };
                });
        } else {
            let calledData = await this.callAPI(opts, conditions, requestVersion);
            // DevExtreme may resolve loads in a different order than they
            // were started. Never let an obsolete load paint over the latest
            // filter result.
            if (requestVersion !== this.listRequestVersion) return this.withAppendRows(this.currentData);
            // null = điều kiện đổi khi đang ở trang > 0: đã về trang đầu và lượt nạp mới sẽ lấy dữ liệu.
            if (calledData === null) return this.withAppendRows(this.currentData);
            result = {
                data: [...calledData.data],
                totalCount: parseInt(calledData.totalCount)
            };

            if (this.allowNew && this.newItems instanceof Array && this.newItems.length > 0) {
                for (let i = 0; i < this.newItems.length; i++) {
                    const n = this.newItems[i];
                    result.data.push(n);
                    result.totalCount++;
                }
            }
            if (this.cachedItems.length > 0)
                result.data = result.data.map((e) => {
                    var obj = this.cachedItems.find((c) => c[keyName] === e[keyName]);
                    return { ...obj, ...e };
                });

            if (this.editedItems.length > 0)
                result.data = result.data.map((e) => {
                    var obj = this.editedItems.find((c) => c[keyName] === e[keyName]);
                    return { ...e, ...obj };
                });
        }

        if (result && result.data instanceof Array) {
            if (this.onFiltering && this.onFiltering.constructor.name == 'Function') result = await this.onFiltering(result);
        }

        if (requestVersion !== this.listRequestVersion) return this.withAppendRows(this.currentData);

        result = this.withAppendRows(result);

        this.isGetLastResult = false;

        if (result && result.data instanceof Array && result.totalCount === undefined) result.totalCount = result.data.length;

        if (this.groupByField) {
            var ar = result && result.data instanceof Array ? result.data : [];

            var groups = AntArray.groupBy(
                ar.map((d) => {
                    return {
                        ...d,
                        key: String(d[this.groupByField])
                    };
                }),
                'key'
            );

            var ar = Object.keys(groups).map((key) => {
                return {
                    key: key,
                    items: groups[key]
                };
            });
            if (this.onGotData) {
                await this.onGotData(
                    {
                        data: ar,
                        totalCount: result.totalCount
                    },
                    this.cachedItems
                );
            }
            return {
                data: ar,
                totalCount: result.totalCount
            };
        }

        if (this.onGotData) {
            await this.onGotData(result, this.cachedItems);
        }

        return result;
    }

    async initConditions(opts) {
        if (this.getPageSize && this.getPageSize.constructor.name == 'Function') this.pageSize = this.getPageSize();
        else if (opts.take !== undefined && opts.take !== null) this.pageSize = opts.take;

        if (this.getSearchText && this.getSearchText.constructor.name == 'Function') this.searchText = this.getSearchText(opts);

        if (this.getPageIndex && this.getPageIndex.constructor.name == 'Function') this.pageIndex = this.getPageIndex(opts);
        else if (opts.take !== undefined && opts.take !== null && opts.skip !== undefined && opts.skip !== null)
            this.pageIndex = parseInt(opts.skip / opts.take);

        if (this.getOrderBy && this.getOrderBy.constructor.name == 'Function') this.orderBy = this.getOrderBy(opts);

        if (this.getOrderType && this.getOrderType.constructor.name == 'Function') this.orderType = this.getOrderType(opts);

        var condsObj = {};

        if (this.getSearchConditions && this.getSearchConditions.constructor.name == 'Function') {
            var obj = this.getSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getParentSearchConditions && this.getParentSearchConditions.constructor.name == 'Function') {
            var obj = this.getParentSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getFilteredGroupConditions && this.getFilteredGroupConditions.constructor.name == 'Function') {
            var obj = this.getFilteredGroupConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        var conditions =
            Object.keys(condsObj).length > 0
                ? Object.keys(condsObj).map((key) => {
                      return {
                          name: key,
                          value: condsObj[key]
                      };
                  })
                : // [
                  //       {
                  //           name: 'conditions',
                  //           value: condsObj
                  //       }
                  //   ]
                  [];

        // if (opts.filter instanceof Array && opts.filter.length > 0) {
        //     if (opts.filter[0] instanceof Array) {
        //         opts.filter.map((f) => {
        //             if (f instanceof Array) {
        //                 conditions.push({
        //                     name: f[0],
        //                     value: f[2]
        //                 });
        //             }
        //         });
        //     } else
        //         conditions.push({
        //             name: opts.filter[0],
        //             value: opts.filter[2]
        //         });
        // }

        if (this.searchText !== undefined && this.searchText !== null && String(this.searchText).trim() !== '')
            conditions.push({
                name: 'searchText',
                value: this.searchText
            });

        if (this.getOrderings && this.getOrderings.constructor.name == 'Function') {
            var obj = this.getOrderings();

            if (obj instanceof Array && obj.length > 0)
                conditions.push({
                    name: 'ordering',
                    value: obj
                });
        }

        if (this.getQueryFields && this.getQueryFields.constructor.name == 'Function') {
            var obj = this.getQueryFields();

            if (obj instanceof Array && obj.length > 0) {
                conditions.push({
                    name: 'selectFields',
                    value: obj
                });
            }
        }
        if (this.orderBy !== undefined && this.orderBy !== null && String(this.orderBy).trim() !== '')
            conditions.push({
                name: 'orderBy',
                value: this.orderBy
            });

        if (this.orderType !== undefined && this.orderType !== null && String(this.orderType).trim() !== '')
            conditions.push({
                name: 'orderType',
                value: this.orderType
            });

        // Khi searchConditions có groupByFields, không gửi paging để backend fetch toàn bộ data cho aggregate
        var hasGroupByFields = condsObj && condsObj.groupByFields instanceof Array && condsObj.groupByFields.length > 0;

        if (this.remotePaging && !hasGroupByFields && this.pageSize !== undefined && this.pageSize !== null)
            conditions.push({
                name: 'pageSize',
                value: this.pageSize
            });

        if (this.remotePaging && !hasGroupByFields && this.pageIndex !== undefined && this.pageIndex !== null)
            conditions.push({
                name: 'pageIndex',
                value: this.pageIndex
            });

        return conditions;
    }

    async callAPI(opts, conditionsSnapshot, requestVersion) {
        if (!this.API || this.allowNew) return;

        // var cleanCached = true;
        // if (this.lastResult) {
        //     var lastConditions = this.lastResult.conditions;
        //     lastConditions = lastConditions.map((c) => {
        //         return {
        //             name: c.name,
        //             value: c.name === 'pageIndex' ? c.value : null
        //         };
        //     });
        //     var lastConditionsHash = crypto
        //         .createHash('md5')
        //         .update(stringify({ ...lastConditions }))
        //         .digest('hex');
        //     var currentConditions = conditions.map((c) => {
        //         return {
        //             name: c.name,
        //             value: c.name === 'pageIndex' ? c.value : null
        //         };
        //     });
        //     var currentConditionsHash = crypto
        //         .createHash('md5')
        //         .update(stringify({ ...currentConditions }))
        //         .digest('hex');
        //     cleanCached = lastConditionsHash !== currentConditionsHash;
        // }

        // var hashKey = crypto.createHash('md5').update(stringify(conditions)).digest('hex');

        var conditions = conditionsSnapshot || (await this.initConditions(opts));
        const forceQueryOptions = this.forceQueryOptions || {};
        this.forceQueryOptions = null;
        const requestConditions = forceQueryOptions.byPassGetCache
            ? [
                  ...conditions,
                  { name: 'byPassGetCache', value: true },
                  { name: 'forceQuery', value: true }
              ]
            : conditions;

        var results = null;

        if (this.onLoading) this.onLoading(conditions);

        var isCleanCached = false;
        if (this.lastResult) {
            var obj1 = {};
            conditions.forEach((c) => {
                if (!['pageIndex'].includes(c.name)) obj1[c.name] = c.value;
            });

            var obj2 = {};
            this.lastResult.conditions.forEach((c) => {
                if (!['pageIndex'].includes(c.name)) obj2[c.name] = c.value;
            });
            isCleanCached = stringify(obj1) !== stringify(obj2);
        }

        if (isCleanCached && this.pageIndex !== 0) {
            if (this.setPageIndex) {
                this.setPageIndex(0);
                return null;
            }
        }

        // Cờ "dùng lại kết quả trước" (sau khi sửa ô, repaint) chỉ hợp lệ khi điều
        // kiện tìm kiếm không đổi. Nếu người dùng vừa chọn searchCategories trong lúc
        // cờ còn bật, trả lastResult sẽ vẽ lại danh sách chưa lọc.
        const isSameAsLastResult = Boolean(this.lastResult) && stringify(conditions) === stringify(this.lastResult.conditions);
        if (this.isGetLastResult && isSameAsLastResult) {
            // && this.lastResult.hashKey && this.lastResult.hashKey === hashKey)
            results = this.lastResult.value;
        } else {
            if (this.url) {
                results = await this.API.getList(requestConditions, this.url, { byPassDefaultConds: true, byPassTotal: Boolean(this.isScrolling) });
            } else if (this.loadFunc) {
                results = await AntAPI.prototype[this.loadFunc].call(this.API, requestConditions, null, { byPassTotal: Boolean(this.isScrolling) });
            } else {
                results = await this.API.getList(requestConditions, null, { byPassTotal: Boolean(this.isScrolling) });
            }
        }

        if (requestVersion !== undefined && requestVersion !== this.listRequestVersion) return this.currentData;

        // `queried: false` means the transport/API request did not yield a
        // valid response. It is fundamentally different from a successful
        // `{ queried: true, rows: [] }` response: keep the current grid data
        // in the former case instead of replacing it with an empty list.
        const hasValidListResponse = results?.queried === true || (results?.queried === undefined && results?.rows instanceof Array);
        if (!hasValidListResponse) {
            return (
                this.currentData || {
                    data: [],
                    totalCount: 0,
                    conditions: conditions
                }
            );
        }

        var pageSize = null;
        var pageIndex = null;
        if (this.remotePaging && this.pageSize !== undefined && this.pageSize !== null) pageSize = this.pageSize;
        if (this.remotePaging && this.pageIndex !== undefined && this.pageIndex !== null) pageIndex = this.pageIndex;

        for (let i = 0; i < results.rows.length; i++) {
            const r = results.rows[i];
            var rIndex = pageSize !== null && pageIndex !== null ? pageSize * pageIndex + i : i;
            r.rowIndex = rIndex + 1;
        }

        var totalCount = results.totalCount !== undefined ? parseInt(results.totalCount) : results.rows.length;

        this.lastResult = {
            // hashKey: hashKey,
            conditions: JSON.parse(stringify(conditions)),
            value: results
        };

        await this.resetCurrentData(
            {
                summary: results.summary,
                data: results.rows,
                totalCount: totalCount,
                conditions: conditions
            },
            isCleanCached
        );

        return {
            summary: results.summary,
            data: results.rows,
            totalCount: totalCount,
            conditions: conditions
        };
    }

    async callDownloadAPI() {
        var opts = this.currentOpts;
        if (this.getSearchText && this.getSearchText.constructor.name == 'Function') this.searchText = this.getSearchText(opts);

        if (this.getOrderBy && this.getOrderBy.constructor.name == 'Function') this.orderBy = this.getOrderBy(opts);

        if (this.getOrderType && this.getOrderType.constructor.name == 'Function') this.orderType = this.getOrderType(opts);

        if (!this.API || this.allowNew) return;

        var condsObj = {};

        if (this.getSearchConditions && this.getSearchConditions.constructor.name == 'Function') {
            var obj = this.getSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }
        if (this.getParentSearchConditions && this.getParentSearchConditions.constructor.name == 'Function') {
            var obj = this.getParentSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getFilteredGroupConditions && this.getFilteredGroupConditions.constructor.name == 'Function') {
            var obj = this.getFilteredGroupConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        var conditions =
            Object.keys(condsObj).length > 0
                ? Object.keys(condsObj).map((key) => {
                      return {
                          name: key,
                          value: condsObj[key]
                      };
                  })
                : // [
                  //       {
                  //           name: 'conditions',
                  //           value: condsObj
                  //       }
                  //   ]
                  [];

        // if (opts && opts.filter instanceof Array && opts.filter.length > 0) {
        //     if (opts.filter[0] instanceof Array) {
        //         opts.filter.map((f) => {
        //             if (f instanceof Array) {
        //                 conditions.push({
        //                     name: f[0],
        //                     value: f[2]
        //                 });
        //             }
        //         });
        //     } else
        //         conditions.push({
        //             name: opts.filter[0],
        //             value: opts.filter[2]
        //         });
        // }

        if (this.searchText !== undefined && this.searchText !== null && String(this.searchText).trim() !== '')
            conditions.push({
                name: 'searchText',
                value: this.searchText
            });

        if (this.getOrderings && this.getOrderings.constructor.name == 'Function') {
            var obj = this.getOrderings();

            if (obj instanceof Array && obj.length > 0)
                conditions.push({
                    name: 'ordering',
                    value: obj
                });
        }

        if (this.getQueryFields && this.getQueryFields.constructor.name == 'Function') {
            var obj = this.getQueryFields({ isDownloading: true });
            if (obj instanceof Array && obj.length > 0) {
                obj = obj.filter((o) => o !== 'checksum');
                conditions.push({
                    name: 'selectFields',
                    value: obj
                });
            }
        }

        if (this.orderBy !== undefined && this.orderBy !== null && String(this.orderBy).trim() !== '')
            conditions.push({
                name: 'orderBy',
                value: this.orderBy
            });

        if (this.orderType !== undefined && this.orderType !== null && String(this.orderType).trim() !== '')
            conditions.push({
                name: 'orderType',
                value: this.orderType
            });

        if (this.url) {
            await this.API.downloadExcel(conditions, this.url);
        }
        // else if (this.loadFunc) await AntAPI.prototype[this.loadFunc].call(this.API, conditions);
        else await this.API.downloadExcel(conditions);
    }

    async callCountAPI(searchCond, opts) {
        var isAllowQuery = true;

        if (!opts || !opts.byPassCheckAllowQuery) {
            if (this.isAllowQuery && this.isAllowQuery.constructor.name == 'Function') isAllowQuery = this.isAllowQuery();

            if (!isAllowQuery) return;
        }

        var opts = this.currentOpts;
        if (this.getSearchText && this.getSearchText.constructor.name == 'Function') this.searchText = this.getSearchText(opts);

        if (!this.API) return;

        var condsObj = {};
        if (this.getSearchConditions && this.getSearchConditions.constructor.name == 'Function') {
            var obj = this.getSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getParentSearchConditions && this.getParentSearchConditions.constructor.name == 'Function') {
            var obj = this.getParentSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        var conditions =
            Object.keys(condsObj).length > 0
                ? Object.keys(condsObj).map((key) => {
                      return {
                          name: key,
                          value: condsObj[key]
                      };
                  })
                : // [
                  //       {
                  //           name: 'conditions',
                  //           value: condsObj
                  //       }
                  //   ]
                  [];

        // if (opts && opts.filter instanceof Array && opts.filter.length > 0) {
        //     if (opts.filter[0] instanceof Array) {
        //         opts.filter.map((f) => {
        //             if (f instanceof Array) {
        //                 conditions.push({
        //                     name: f[0],
        //                     value: f[2]
        //                 });
        //             }
        //         });
        //     } else
        //         conditions.push({
        //             name: opts.filter[0],
        //             value: opts.filter[2]
        //         });
        // }

        if (this.searchText !== undefined && this.searchText !== null && String(this.searchText).trim() !== '')
            conditions.push({
                name: 'searchText',
                value: this.searchText
            });

        if (searchCond) {
            Object.keys(searchCond).forEach(function (key) {
                if (searchCond[key] !== undefined)
                    conditions.push({
                        name: key,
                        value: searchCond[key]
                    });
            });
        }
        var result = await this.API.getCount(conditions);
        if (result && !result.error && result.data) return result.data.count;
        return 0;
    }

    async callCountsAPI(counts, opts) {
        var isAllowQuery = true;

        if (!opts || !opts.byPassCheckAllowQuery) {
            if (this.isAllowQuery && this.isAllowQuery.constructor.name == 'Function') isAllowQuery = this.isAllowQuery();

            if (!isAllowQuery) return;
        }

        var opts = this.currentOpts;
        if (this.getSearchText && this.getSearchText.constructor.name == 'Function') this.searchText = this.getSearchText(opts);

        if (!this.API) return;

        var condsObj = {};
        if (this.getSearchConditions && this.getSearchConditions.constructor.name == 'Function') {
            var obj = this.getSearchConditions({ byPassCheckActions: true });

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        if (this.getParentSearchConditions && this.getParentSearchConditions.constructor.name == 'Function') {
            var obj = this.getParentSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined) condsObj[key] = obj[key];
                    // conditions.push({
                    //     name: key,
                    //     value: obj[key]
                    // });
                });
            }
        }

        var conditions =
            Object.keys(condsObj).length > 0
                ? Object.keys(condsObj).map((key) => {
                      return {
                          name: key,
                          value: condsObj[key]
                      };
                  })
                : // [
                  //       {
                  //           name: 'conditions',
                  //           value: condsObj
                  //       }
                  //   ]
                  [];

        // if (opts && opts.filter instanceof Array && opts.filter.length > 0) {
        //     if (opts.filter[0] instanceof Array) {
        //         opts.filter.map((f) => {
        //             if (f instanceof Array) {
        //                 conditions.push({
        //                     name: f[0],
        //                     value: f[2]
        //                 });
        //             }
        //         });
        //     } else
        //         conditions.push({
        //             name: opts.filter[0],
        //             value: opts.filter[2]
        //         });
        // }

        if (this.searchText !== undefined && this.searchText !== null && String(this.searchText).trim() !== '')
            conditions.push({
                name: 'searchText',
                value: this.searchText
            });

        if (counts) {
            conditions.push({
                name: 'countGroups',
                value: counts
            });
        }
        var result = await this.API.getCounts(conditions);
        if (result && !result.error && result.data) return result.data;
        return [];
    }

    async callScanBarcode(opts) {
        if (!this.API) return;

        var conditions = [];
        if (this.getParentSearchConditions && this.getParentSearchConditions.constructor.name == 'Function') {
            var obj = this.getParentSearchConditions();

            if (obj) {
                Object.keys(obj).forEach(function (key) {
                    if (obj[key] !== undefined)
                        conditions.push({
                            name: key,
                            value: obj[key]
                        });
                });
            }
        }

        if (this.getScanBarcode && this.getScanBarcode.constructor.name == 'Function') this.scanBarcode = this.getScanBarcode(opts);

        if (this.scanBarcode !== undefined && this.scanBarcode !== null && String(this.scanBarcode).trim() !== '') {
            conditions.push({
                name: 'scanBarcode',
                value: this.scanBarcode
            });

            return await this.API.postScanBarcode(conditions);
        }
    }

    async resetEditedItem(keyValue, opts) {
        var keyName = this.options.key;
        var cached = null;
        var edited = null;
        if (this.cachedItems instanceof Array) cached = this.cachedItems.find((c) => c && c[keyName] === keyValue);
        if (this.editedItems instanceof Array) edited = this.editedItems.find((c) => c && c[keyName] === keyValue);

        if (cached && edited) {
            var values = {};
            Object.keys(edited).forEach(function (field) {
                if (cached[field] === undefined) {
                    edited[field] = undefined;
                } else if (edited[field] !== cached[field]) {
                    edited[field] = cached[field];
                    values[field] = cached[field];
                }
            });

            await this.cleanEditedItem(keyValue);

            if ((!opts || !opts.byPassEvent) && this.onUpdated) this.onUpdated(keyValue, values);
        }

        return cached;
    }

    async cleanEditedItem(keyValue) {
        var keyName = this.options.key;
        var cached = null;
        var edited = null;
        if (this.cachedItems instanceof Array) cached = this.cachedItems.find((c) => c && c[keyName] === keyValue);
        if (this.editedItems instanceof Array) edited = this.editedItems.find((c) => c && c[keyName] === keyValue);

        if (cached && edited) {
            var isMatched = true;
            Object.keys(edited).forEach(function (field) {
                if (edited[field] !== cached[field]) isMatched = false;
            });
            if (isMatched) {
                var index = this.editedItems.indexOf(edited);
                if (index > -1) {
                    this.editedItems.splice(index, 1);
                }
            }
        }
    }

    async updateItem(keyValue, values, opts) {
        await this.updateDataSource(keyValue, values, opts);
    }

    async updateDataSource(keyValue, values, opts) {
        this.willGetLastResult();
        var keyName = this.options.key;

        if (this.updateMode === 'local') {
            if (this.editedItems instanceof Array) {
                var isExisted = this.editedItems.some((e) => e[keyName] === keyValue);
                if (isExisted) {
                    this.editedItems.forEach((e) => {
                        if (e[keyName] === keyValue) {
                            Object.keys(values).forEach(function (field) {
                                e[field] = values[field];
                            });
                        }
                    });
                    await this.cleanEditedItem(keyValue);
                } else {
                    var obj = {
                        [keyName]: keyValue
                    };

                    if (this.currentData.data instanceof Array) {
                        this.currentData.data.forEach((r) => {
                            if (r && r[keyName] === keyValue) {
                                Object.keys(r).forEach(function (field) {
                                    if (Object.keys(obj).indexOf(field) <= -1) obj[field] = r[field];
                                });
                            }
                        });
                    }

                    if (this.cachedItems instanceof Array) {
                        this.cachedItems.forEach((r) => {
                            if (r && r[keyName] === keyValue) {
                                Object.keys(r).forEach(function (field) {
                                    if (Object.keys(obj).indexOf(field) <= -1) obj[field] = r[field];
                                });
                            }
                        });
                    }

                    this.editedItems.push({ ...obj, ...values });
                }

                if (this.allowNew) {
                    var lastItem = null;
                    if (this.cachedItems instanceof Array && this.cachedItems.length > 0) {
                        var lastItem = this.cachedItems[this.cachedItems.length - 1];
                        if (lastItem && lastItem[keyName] === keyValue) {
                            this.newItems.push({ [keyName]: AntTime.nowUnix() });
                        }
                    }

                    if (!lastItem && this.newItems instanceof Array && this.newItems.length > 0) {
                        lastItem = this.newItems[this.newItems.length - 1];
                        if (lastItem && lastItem[keyName] === keyValue) {
                            this.newItems.push({ [keyName]: AntTime.nowUnix() });
                        }
                    }
                }
            }
        }

        if ((!opts || !opts.byPassEvent) && this.onUpdated) this.onUpdated(keyValue, values);
    }

    reInit(args) {
        this.config = args;
        this.getSearchConditions = args.getSearchConditions;
        this.getParentSearchConditions = args.getParentSearchConditions;
        this.getFilteredGroupConditions = args.getFilteredGroupConditions;
        this.getOrderings = args.getOrderings;
        this.getQueryFields = args.getQueryFields;
        this.getSearchText = args.getSearchText;
        this.getScanBarcode = args.getScanBarcode;
        this.getPageSize = args.getPageSize;
        this.getPageIndex = args.getPageIndex;
        this.setPageIndex = args.setPageIndex;
        this.getOrderBy = args.getOrderBy;
        this.getOrderType = args.getOrderType;
        this.isAllowQuery = args.isAllowQuery;
        this.isShowEdittedRowsOnly = args.isShowEdittedRowsOnly;
        this.onGotData = args.onGotData;
        this.onLoaded = args.onLoaded;
        this.onLoading = args.onLoading;
        this.onFiltering = args.onFiltering;
        // Lưới nhập trực tiếp: dòng nháp chèn cuối trang (không tính vào totalCount/currentData).
        this.getAppendRows = args.getAppendRows;
        this.onUpdated = args.onUpdated;

        this.isScrolling = args.isScrolling !== undefined ? args.isScrolling : false;
        this.remotePaging = args.remotePaging !== undefined ? args.remotePaging : true;
        this.allowNew = args.allowNew !== undefined ? args.allowNew : false;
        this.updateMode = args.updateMode !== undefined ? args.updateMode : 'local';
        this.loadFunc = args.loadFunc ? args.loadFunc : 'getList';

        this.groupByField = args.groupByField;

        this.API = args.API;
        this.url = args.url;

        this.options = {
            key: args.key ? args.key : 'id',
            load: this.loadDataSource,
            update: this.updateDataSource,
            byKey: this.getItemByKey
            // cacheRawData:false
        };

        // if (this.loadTimeout) clearTimeout(this.loadTimeout);
        // this.loadTimeout = setTimeout(
        //     (that) => {
        //         if (!that.dataSource) that.dataSource = new CustomStore(that.options);
        //         else that.dataSource.load(that.options);
        //     },
        //     1000,
        //     this
        // );

        if (!this.dataSource) this.dataSource = new CustomStore(this.options);
        else {
            if (args.forceReload) this.dataSource.load(this.options);
        }
    }
}
